<template>
  <el-input
    ref="input"
    type="text"
    :value="value"
    :style="inputStyle"
    v-bind="$attrs"
    @input="handleInput"
    @blur="handleBlur"
    @focus="handleFocus"
    @change="handleChange"
    @clear="handleClear"
  />
</template>

<script>
export default {
  name: "AmountInput",
  inheritAttrs: false,
  props: {
    /** v-model 绑定值 */
    value: {
      type: [String, Number],
      default: "",
    },
    /** 小数位数，默认 2 位 */
    precision: {
      type: Number,
      default: 2,
      validator: (val) => val >= 0 && Number.isInteger(val),
    },
    /** 失焦时是否格式化为固定小数位 */
    formatOnBlur: {
      type: Boolean,
      default: true,
    },
    /** 是否允许负数 */
    allowNegative: {
      type: Boolean,
      default: false,
    },
    /** 最大值 */
    max: {
      type: [Number, String],
      default: undefined,
    },
    /** 最小值 */
    min: {
      type: [Number, String],
      default: undefined,
    },
    /** 输入框宽度 */
    width: {
      type: [String, Number],
      default: "",
    },
  },
  computed: {
    inputStyle() {
      const style = { ...this.$attrs.style };
      
      // 处理宽度
      if (this.width) {
        if (typeof this.width === 'number') {
          style.width = `${this.width}px`;
        } else {
          // 支持带单位的字符串，如 '150px', '50%', '20rem'
          style.width = this.width;
        }
      }
      
      return Object.keys(style).length > 0 ? style : null;
    },
  },
  methods: {
    focus() {
      this.$refs.input.focus();
    },
    blur() {
      this.$refs.input.blur();
    },
    select() {
      this.$refs.input.select();
    },
    handleInput(value) {
      let nextValue = this.filterValue(value);
      nextValue = this.limitRange(nextValue);
      if (nextValue !== this.normalizeValue(this.value)) {
        this.$emit("input", nextValue);
      }
    },
    handleBlur(event) {
      if (this.formatOnBlur) {
        const formatted = this.formatValue(this.value);
        if (formatted !== this.normalizeValue(this.value)) {
          this.$emit("input", formatted);
        }
      }
      this.$emit("blur", event);
    },
    handleFocus(event) {
      this.$emit("focus", event);
    },
    handleChange(value) {
      this.$emit("change", value);
    },
    handleClear() {
      this.$emit("clear");
    },
    normalizeValue(value) {
      if (value === null || value === undefined) {
        return "";
      }
      return String(value);
    },
    filterValue(value) {
      let raw = this.normalizeValue(value);
      if (raw === "") {
        return "";
      }

      let negative = false;
      if (this.allowNegative) {
        negative = raw.startsWith("-");
        raw = raw.replace(/-/g, "");
      }

      const precision = Math.max(0, this.precision);
      const decimalPart =
        precision > 0 ? `(?:\\.\\d{0,${precision}})?` : "";
      const pattern = new RegExp(`^\\D*(\\d*${decimalPart}).*$`, "g");
      raw = raw.replace(pattern, "$1");

      if (negative && raw !== "") {
        return `-${raw}`;
      }
      if (negative && raw === "") {
        return "-";
      }
      return raw;
    },
    formatValue(value) {
      const raw = this.normalizeValue(value);
      if (raw === "" || raw === "-" || raw === ".") {
        return "";
      }

      const num = Number(raw);
      if (Number.isNaN(num)) {
        return "";
      }

      return num.toFixed(this.precision);
    },
    limitRange(value) {
      if (value === "" || value === "-" || value === ".") {
        return value;
      }

      const num = Number(value);
      if (Number.isNaN(num)) {
        return value;
      }

      if (this.max !== undefined && num > Number(this.max)) {
        return this.normalizeValue(this.max);
      }
      if (this.min !== undefined && num < Number(this.min)) {
        return this.normalizeValue(this.min);
      }

      return value;
    },
  },
};
</script>
